// lib/device/keys.ts
var DB_NAME = "lune-auth-device";
var STORE_NAME = "keypairs";
var RECORD_KEY = "default";
var toBase64Url = (buf) => {
  const bytes = new Uint8Array(buf);
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};
var toHex = (buf) => Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join("");
var openDB = async () => await new Promise((resolve, reject) => {
  const req = indexedDB.open(DB_NAME, 1);
  req.onupgradeneeded = () => {
    req.result.createObjectStore(STORE_NAME);
  };
  req.onsuccess = () => {
    resolve(req.result);
  };
  req.onerror = () => {
    console.debug("openDB/auth error", req.error);
    reject(req.error);
  };
});
var loadStoredKeyPair = async () => {
  const db = await openDB();
  return await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const req = tx.objectStore(STORE_NAME).get(RECORD_KEY);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
};
var storeKeyPair = async (pair) => {
  const db = await openDB();
  await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).put(pair, RECORD_KEY);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
};
var generateKeyPair = async () => await crypto.subtle.generateKey(
  { name: "ECDSA", namedCurve: "P-256" },
  false,
  // non-extractable: the private key never leaves this browser, not even to our own JS
  ["sign", "verify"]
);
var keyPairPromise;
var getOrCreateKeyPair = async () => {
  keyPairPromise ??= (async () => {
    const stored = await loadStoredKeyPair();
    if (stored != null) return stored;
    const pair = await generateKeyPair();
    await storeKeyPair(pair);
    return pair;
  })();
  return await keyPairPromise;
};
var currentDeviceId = async () => {
  const { publicKey } = await getOrCreateKeyPair();
  return await fingerprint(publicKey);
};
var publicKeyRawB64 = async (publicKey) => {
  const raw = await crypto.subtle.exportKey("raw", publicKey);
  return toBase64Url(raw);
};
var fingerprint = async (publicKey) => {
  const raw = await crypto.subtle.exportKey("raw", publicKey);
  const digest = await crypto.subtle.digest("SHA-256", raw);
  return toBase64Url(digest).slice(0, 20);
};
var canonicalString = async (timestamp, method, path, body) => {
  const bodyBytes = new TextEncoder().encode(body ?? "");
  const digest = await crypto.subtle.digest("SHA-256", bodyBytes);
  return `${timestamp}
${method}
${path}
${toHex(digest)}`;
};
var signCanonical = async (privateKey, canonical) => {
  const data = new TextEncoder().encode(canonical);
  const signature = await crypto.subtle.sign(
    { name: "ECDSA", hash: "SHA-256" },
    privateKey,
    data
  );
  return toBase64Url(signature);
};

// lib/device/protocol.ts
var HeaderDeviceID = "X-Device-Id";
var HeaderDeviceSignature = "X-Device-Signature";
var HeaderDeviceTimestamp = "X-Device-Timestamp";
var HeaderDevicePublicKey = "X-Device-Public-Key";

// lib/device/sign.ts
var requestPath = (input) => {
  if (typeof input === "string") {
    return new URL(input, globalThis.location?.href ?? "http://localhost").pathname;
  }
  if (input instanceof URL) return input.pathname;
  return new URL(input.url, globalThis.location?.href ?? "http://localhost").pathname;
};
var requestBody = (init) => {
  const body = init?.body;
  return typeof body === "string" ? body : void 0;
};
var buildDeviceHeaders = async (method, path, body) => {
  const { publicKey, privateKey } = await getOrCreateKeyPair();
  const id = await fingerprint(publicKey);
  const timestamp = Math.floor(Date.now() / 1e3).toString();
  const canonical = await canonicalString(timestamp, method, path, body);
  const signature = await signCanonical(privateKey, canonical);
  return {
    [HeaderDeviceID]: id,
    [HeaderDeviceSignature]: signature,
    [HeaderDeviceTimestamp]: timestamp,
    // Always attached, not just on a device's very first request: the server only ever
    // trusts this header when it has no stored key for the id yet (see
    // device.resolveDevicePublicKey), so re-sending it is harmless and keeps this client
    // simple -- no "have we announced yet" state to track.
    [HeaderDevicePublicKey]: await publicKeyRawB64(publicKey)
  };
};
var signOnce = async (input, init) => {
  try {
    const method = (init?.method ?? "GET").toUpperCase();
    const path = requestPath(input);
    const body = requestBody(init);
    const deviceHeaders = await buildDeviceHeaders(method, path, body);
    const headers = new Headers(init?.headers);
    for (const [name, value] of Object.entries(deviceHeaders)) {
      headers.set(name, value);
    }
    return await fetch(input, { ...init, headers });
  } catch (err) {
    console.debug("signOnce failed", err);
    return Promise.reject("signOnce failed" + err);
  }
};
var signedFetch = async (input, init) => {
  const response = await signOnce(input, init);
  if (response.redirected) {
    return await signOnce(response.url, init);
  }
  return response;
};

export {
  currentDeviceId,
  signedFetch
};
